import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { borderCss, decorationSvg, svgDataUrl } from '../../designs/decorations';
import { fontStack, getFont } from '../../designs/fonts';
import { lineCss, paperCss, type Look } from '../../designs/look';
import { drawStrokes } from '../../drawing/render';
import { visibleLines } from '../../notes/model';
import type { Line, Note, NoteImage, Stroke } from '../../storage/schemas';
import { CheckboxGlyph } from './Checkbox';
import { useAssetUrl } from './useAssetUrl';
import './note.css';

export const NOTE_ASPECT = 2.2;

export function cardAspect(note: Pick<Note, 'style'>): number {
  return note.style.compact ? NOTE_ASPECT * 2 : NOTE_ASPECT;
}

export function cardVars(look: Look, note: Pick<Note, 'style'>): CSSProperties {
  const pitch = 5 * look.patternScale; // cqw — reference: width × 0.05
  const font = getFont(look.font);
  // Reference: the pattern sits in an inset box starting 5% from the left/top edges; whole cells only.
  const cols = Math.floor(90 / pitch + 1e-6);
  const rows = Math.max(1, Math.floor((100 / cardAspect(note) - 10) / pitch + 1e-6));
  return {
    '--paper': paperCss(look),
    '--line': lineCss(look),
    '--gx': `${(100 - cols * pitch) / 2}cqw`,
    '--gw': `${cols * pitch}cqw`,
    '--gh': `${rows * pitch}cqw`,
    '--ink': look.ink,
    '--accent': look.accent,
    '--pitch': `${pitch}cqw`,
    '--fs': `${(note.style.fontScale * font.scale).toFixed(3)}cqw`,
    '--lh': String(note.style.lineHeight),
    '--note-font': fontStack(look.font),
    '--ar': String(cardAspect(note)),
  } as CSSProperties;
}

interface NoteCardProps {
  note: Pick<Note, 'style' | 'images' | 'strokes' | 'lines' | 'id'>;
  look: Look;
  className?: string;
  /** Replaces the static text layer (the editor passes its editable lines). */
  textLayer?: ReactNode;
  /** Replaces the static image layer (the editor passes interactive images). */
  imageLayer?: (above: boolean) => ReactNode;
  /** Replaces the static drawing layer. */
  drawingLayer?: ReactNode;
  overlay?: ReactNode;
  onToggleLine?: (line: Line) => void;
  label?: string;
  style?: CSSProperties;
}

export function NoteCard({ note, look, className, textLayer, imageLayer, drawingLayer, overlay, onToggleLine, label, style }: NoteCardProps) {
  const aspect = cardAspect(note);
  const deco = decorationSvg(look.decoration, look.accent, look.line, aspect);
  const border = borderCss(look.border);
  return (
    <div className={`note-frame ${className ?? ''}`} style={{ ...cardVars(look, note), ...style }}>
    <div
      className={`note-card pattern-${look.pattern} ${note.style.compact ? 'is-compact' : ''} ${look.paperAlpha !== undefined && look.paperAlpha < 1 ? 'is-clear' : ''}`}
      role={label ? 'img' : undefined}
      aria-label={label}
    >
      <div className="nc-paper" aria-hidden="true">
        {look.pattern !== 'plain' && <div className="nc-pattern" />}
      </div>
      {deco && <img className="nc-deco" src={svgDataUrl(deco)} alt="" aria-hidden="true" draggable={false} />}
      {border && <div className="nc-border" style={border} aria-hidden="true" />}
      {imageLayer ? imageLayer(false) : <StaticImages images={note.images} above={false} aspect={aspect} />}
      {textLayer ?? <StaticLines note={note} ink={look.ink} onToggle={onToggleLine} />}
      {imageLayer ? imageLayer(true) : <StaticImages images={note.images} above aspect={aspect} />}
      {drawingLayer ?? (note.strokes.length > 0 && <StaticStrokes strokes={note.strokes} />)}
      {note.style.watermark && <Watermark />}
      {overlay}
    </div>
    </div>
  );
}

export function Watermark() {
  // Original MemoNest signature, sized like the reference's (≈ width / 12) and tinted with the accent colour.
  return (
    <div className="nc-watermark" aria-hidden="true">
      <span>Memo</span>
      <span>Nest</span>
    </div>
  );
}

function lineClass(l: Line, completedMode: string): string {
  const m = l.marks ?? {};
  return [
    'nc-line',
    l.kind === 'check' ? 'is-check' : '',
    l.kind === 'check' && l.checked && completedMode !== 'hide' ? 'is-done' : '',
    m.bold ? 'm-bold' : '',
    m.italic ? 'm-italic' : '',
    m.underline ? 'm-underline' : '',
    m.heading ? 'm-heading' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function StaticLines({ note, ink, onToggle }: { note: Pick<Note, 'lines' | 'style'>; ink: string; onToggle?: (l: Line) => void }) {
  const lines = visibleLines(note, false);
  return (
    <div className={`nc-text align-${note.style.align} valign-${note.style.vAlign}`}>
      <div className="nc-lines">
      {lines.map((l) => (
        <div key={l.id} className={lineClass(l, note.style.completed)}>
          {l.kind === 'check' &&
            (onToggle ? (
              <button type="button" className="nc-check" role="checkbox" aria-checked={l.checked} aria-label={l.text || 'Checklist item'} onClick={() => onToggle(l)}>
                <CheckboxGlyph checked={l.checked} color={ink} />
              </button>
            ) : (
              <span className="nc-check" aria-hidden="true">
                <CheckboxGlyph checked={l.checked} color={ink} />
              </span>
            ))}
          <span className="nc-line-text">{l.text || '​'}</span>
        </div>
      ))}
      </div>
    </div>
  );
}

export function imageBoxStyle(img: NoteImage, aspect: number): CSSProperties {
  const w = img.w * 100;
  const h = img.w * img.aspect * 100;
  return {
    width: `${w}cqw`,
    height: `${h}cqw`,
    left: `calc(${img.x * 100}cqw - ${w / 2}cqw)`,
    top: `calc(${(img.y * 100) / aspect}cqw - ${h / 2}cqw)`,
    transform: `rotate(${img.rotation}deg)`,
    zIndex: img.z,
  };
}

export function cropClip(img: NoteImage): string {
  const c = img.crop;
  return `inset(${c.t * 100}% ${c.r * 100}% ${c.b * 100}% ${c.l * 100}%)`;
}

function StaticImage({ img, aspect }: { img: NoteImage; aspect: number }) {
  const url = useAssetUrl(img.assetId);
  return (
    <div className="nc-image" style={imageBoxStyle(img, aspect)}>
      {url && <img src={url} alt="" draggable={false} style={{ clipPath: cropClip(img) }} />}
    </div>
  );
}

function StaticImages({ images, above, aspect }: { images: NoteImage[]; above: boolean; aspect: number }) {
  const list = images.filter((i) => i.aboveText === above).sort((a, b) => a.z - b.z);
  if (!list.length) return null;
  return (
    <div className={`nc-images ${above ? 'is-above' : 'is-below'}`}>
      {list.map((img) => (
        <StaticImage key={img.id} img={img} aspect={aspect} />
      ))}
    </div>
  );
}

export function StaticStrokes({ strokes }: { strokes: Stroke[] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const paint = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);
      drawStrokes(ctx, strokes, rect.width);
    };
    paint();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(paint) : null;
    ro?.observe(canvas);
    return () => ro?.disconnect();
  }, [strokes]);
  return <canvas ref={ref} className="nc-strokes" aria-hidden="true" />;
}
